import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { HelperService } from '../services/helper.service';

/**
 * Route-level permission gate.
 *
 * Annotate a route with the slug it requires:
 *   { path: 'tickets', component: TicketsComponent,
 *     canActivate: [AuthGuard, PermissionGuard],
 *     data: { permission: 'TICKET-VIEW-ALL' } }
 *
 * `data.permission` also accepts an array, treated as OR (any one grants access) —
 * the common VIEW-OWN / VIEW-ALL pair.
 *
 * A route with no `data.permission` is allowed, so adding the guard to a route
 * that has not been annotated yet changes nothing. This lets routes be annotated
 * incrementally instead of in one risky sweep.
 *
 * This is UX, not enforcement — the backend authorizes every request regardless.
 */
@Injectable({ providedIn: 'root' })
export class PermissionGuard {
  constructor(
    private router: Router,
    private helperService: HelperService
  ) {}

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    if (this.isAllowed(next)) {
      return true;
    }
    this.router.navigate(['/access-denied']);
    return false;
  }

  canActivateChild(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return this.canActivate(next, state);
  }

  /** Shared by the guard and the on-change re-check in PermissionRouteWatcher. */
  isAllowed(snapshot: ActivatedRouteSnapshot): boolean {
    const required = snapshot?.data?.['permission'];
    if (!required) {
      return true;
    }

    const slugs: string[] = Array.isArray(required) ? required : [required];
    if (slugs.length === 0) {
      return true;
    }

    return slugs.some((slug) => this.helperService.isPermissionAvailable(slug));
  }
}
